import { Fragment } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import ArtifactVersionBadge from "@/components/evidenceGathering/ArtifactVersionBadge";
import { normalizeArtifactType } from "@/components/evidenceGathering/ArtifactTypeBadge";
import { groupArtifactsBySheet, isSheetRow, type SheetGroupable } from "@/lib/artifacts";

export interface ArtifactChecklistItem extends SheetGroupable {
  uploadVersion?: number | null;
  /** Whether this upload adds or changes the artefact. Supplied by the preview
   *  API for upload rows; the catalog derives it from the project's highest
   *  version instead, since nothing is being uploaded there. */
  changed?: boolean;
}

interface ArtifactChecklistProps {
  items: ArtifactChecklistItem[];
  selectedUids: string[];
  onSelectionChange: (uids: string[]) => void;
  /** Supplied only by the reselect catalog, where rows are real persisted
   *  artifacts that can be discarded. The upload preview has nothing to delete
   *  yet, so it omits this and the column doesn't render. */
  onDelete?: (item: ArtifactChecklistItem) => void;
  deletingUids?: Set<string>;
}

// Shared between the upload-preview checklist (UploadCemModel) and the
// reselect-without-upload catalog (ArtifactCatalogDialog) — one place to group
// artefacts by their sheet and render the checkbox/ID/name/type/version row.
const ArtifactChecklist = ({
  items,
  selectedUids,
  onSelectionChange,
  onDelete,
  deletingUids,
}: ArtifactChecklistProps) => {
  const groups = groupArtifactsBySheet(items);
  const columnCount = onDelete ? 6 : 5;

  // Sheet rows are headers, not artefacts — counting them would put the
  // select-all checkbox permanently out of reach.
  const selectableUids = items.filter((item) => !isSheetRow(item)).map((item) => item.uid);
  const selectedCount = selectedUids.filter((uid) => selectableUids.includes(uid)).length;
  const allSelected = selectableUids.length > 0 && selectedCount === selectableUids.length;

  const toggle = (uid: string, checked: boolean) => {
    onSelectionChange(
      checked ? [...selectedUids, uid] : selectedUids.filter((id) => id !== uid)
    );
  };

  return (
    <div className="rounded-md border overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10">
              <Checkbox
                checked={allSelected}
                onCheckedChange={(checked) => onSelectionChange(checked === true ? selectableUids : [])}
                aria-label="Select all artefacts"
              />
            </TableHead>
            <TableHead className="w-40">ID</TableHead>
            <TableHead>Artefact Name</TableHead>
            <TableHead className="w-24">Type</TableHead>
            <TableHead className="w-32">Upload Version</TableHead>
            {onDelete && <TableHead className="w-12" />}
          </TableRow>
        </TableHeader>
        <TableBody>
          {groups.map((group) => (
            <Fragment key={group.key}>
              <TableRow className="bg-muted/40 hover:bg-muted/40">
                <TableCell colSpan={columnCount} className="py-2 text-sm font-semibold text-foreground">
                  {group.tabNumber === Number.MAX_SAFE_INTEGER
                    ? group.label
                    : `${group.tabNumber}. ${group.label}`}
                </TableCell>
              </TableRow>

              {group.leaves.map((item) => {
                return (
                  <TableRow
                    key={item.uid}
                    className="cursor-pointer"
                    onClick={() => toggle(item.uid, !selectedUids.includes(item.uid))}
                  >
                    {/* The row itself toggles, so the checkbox must not also
                        handle the same click — it would toggle twice. */}
                    <TableCell className="py-2" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedUids.includes(item.uid)}
                        onCheckedChange={(checked) => toggle(item.uid, checked === true)}
                      />
                    </TableCell>
                    <TableCell className="py-2 text-sm text-muted-foreground truncate">{item.uid}</TableCell>
                    <TableCell className="py-2 text-sm">{item.name}</TableCell>
                    <TableCell className="py-2 text-sm text-muted-foreground">
                      {normalizeArtifactType(item.type) ?? item.type}
                    </TableCell>
                    <TableCell className="py-2">
                      {/* Highlighted when the latest upload is what put this
                          artefact here — added or changed. Not "anything past
                          v1", which was the old rule and could never fire
                          because the version was a hardcoded string. */}
                      <ArtifactVersionBadge
                        uploadVersion={item.uploadVersion}
                        highlight={item.changed ?? false}
                      />
                    </TableCell>
                    {onDelete && (
                      <TableCell className="py-2" onClick={(e) => e.stopPropagation()}>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          title="Delete artefact"
                          className="w-7 h-7 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                          disabled={deletingUids?.has(item.uid)}
                          onClick={() => onDelete(item)}
                        >
                          {deletingUids?.has(item.uid) ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </Fragment>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default ArtifactChecklist;
